import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { getTierColor, getTierIcon } from './ui/tierUtil';
import { useAuth } from '../Services/AuthContext';

import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Switch } from './ui/switch';
import { Alert, AlertDescription } from './ui/alert';

import { 
  User, 
  Mail, 
  Building2, 
  Calendar, 
  Bell, 
  Edit,
  Save,
  Camera,
  Lock,
  Check
} from 'lucide-react';

export function Profile() {
  
  const { idToken, currentPartner, appUser } = useAuth();

  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState(appUser);

  const [notifications, setNotifications] = useState({
    pricelistChanges: true,
    contentUploads: true,
    generalAnnouncements: true,
    pushNotifications: true,
    highPriorityOnly: false
  });

  const [activeTab, setActiveTab] = useState('details');
  const [profileSaved, setProfileSaved] = useState(false);

  const handleSaveProfile = () => {
    console.log('Saving profile:', editedProfile);
    // Update user context with new information
    if (updateUser) {
      updateUser(currentUser.id, {
        name: editedProfile.name,
        email: editedProfile.email
      });
    }
    setIsEditing(false);
    setProfileSaved(true);
    setTimeout(() => setProfileSaved(false), 3000);
  };

  const TierIcon = getTierIcon(currentPartner.tier);

  return (
    <div className="space-y-6 animate-fade-in-fast">
      {/* Header */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1>My Profile</h1>
            <p className="text-muted-foreground mt-2">
              Manage your account settings and preferences
            </p>
          </div>
          <div className="flex items-center gap-2">
            <User className="w-5 h-5 text-worldsview-blue" />
            <Badge className="bg-worldsview-blue text-white">Partner</Badge>
          </div>
        </div>
      </div>

      {/* Profile Overview */}
      <Card className="glass-card">
        <CardContent className="pt-6">
          <div className="flex items-start gap-6">
            <div className="relative">
              <div className="w-24 h-24 rounded-full bg-worldsview-blue flex items-center justify-center text-white text-2xl font-bold">
                {appUser.first_name.charAt(0)}{appUser.last_name.charAt(0)}
              </div>
            </div>
            
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h2>{appUser.first_name} {appUser.last_name}</h2>
                <Badge 
                  className="text-white flex items-center gap-1"
                  style={{ backgroundColor: getTierColor(currentPartner.tier) }}
                >
                  <TierIcon className="w-3 h-3" />
                  {currentPartner.tier}
                </Badge>
                <Badge variant="outline" className="glass-badge">
                  {appUser.roles[0].name}
                </Badge>
              </div>
              
              <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                <div className="flex items-center gap-1">
                  <Mail className="w-4 h-4" />
                  {appUser.email}
                </div>
                <div className="flex items-center gap-1">
                  <Building2 className="w-4 h-4" />
                  {currentPartner.name || 'Worldsview Internal'}
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Joined {appUser.created_at}
                </div>
              </div>
              
              
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Success Alert */}
      {profileSaved && (
        <Alert className="glass-card border-worldsview-green bg-worldsview-green/5">
          <Check className="h-4 w-4 text-worldsview-green" />
          <AlertDescription className="text-worldsview-green">
            Profile updated successfully!
          </AlertDescription>
        </Alert>
      )}

      {/* Profile Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <div className="glass-card">
          <CardContent className="pt-6">
            <TabsList className="grid w-full grid-cols-1">
              <TabsTrigger value="details">Personal Details</TabsTrigger>
              {/* <TabsTrigger value="notifications">Notifications</TabsTrigger> */}
            </TabsList>
          </CardContent>
        </div>

        {/* Personal Details Tab */}
        <TabsContent value="details">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-worldsview-blue" />
                Personal Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={`${editedProfile.first_name} ${editedProfile.last_name}`}
                    onChange={(e) => setEditedProfile({...editedProfile, name: e.target.value})}
                    disabled={!isEditing}
                    className="glass-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="flex items-center gap-2">
                    Email Address
                    <Lock className="w-3 h-3 text-muted-foreground" />
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={editedProfile.email}
                    onChange={(e) => setEditedProfile({...editedProfile, email: e.target.value})}
                    disabled={true}
                    className="glass-input bg-muted/20 cursor-not-allowed"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    value={editedProfile.phone}
                    onChange={(e) => setEditedProfile({...editedProfile, phone: e.target.value})}
                    disabled={!isEditing}
                    className="glass-input"
                  />
                </div>
                
              </div>

              {/* Read-only Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t border-border">
                <div className="space-y-2">
                  <Label>User ID</Label>
                  <Input
                    value={appUser.id}
                    disabled
                    className="glass-input bg-muted/50"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Account Created</Label>
                  <Input
                    value={appUser.created_at}
                    disabled
                    className="glass-input bg-muted/50"
                  />
                </div>
                
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-worldsview-orange" />
                Notification Preferences
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Content Notification Categories */}
              <div className="space-y-4">
                <div className="pb-2 border-b border-border">
                  <h4 className="font-medium text-sm">Content & Updates</h4>
                  <p className="text-xs text-muted-foreground">Choose what content notifications you'd like to receive</p>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h4>Pricelist Changes</h4>
                    <p className="text-sm text-muted-foreground">
                      Get notified when pricing is updated
                    </p>
                  </div>
                  <Switch
                    checked={notifications.pricelistChanges}
                    onCheckedChange={(checked) => setNotifications({...notifications, pricelistChanges: checked})}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h4>Content Uploads</h4>
                    <p className="text-sm text-muted-foreground">
                      New training materials and resources
                    </p>
                  </div>
                  <Switch
                    checked={notifications.contentUploads}
                    onCheckedChange={(checked) => setNotifications({...notifications, contentUploads: checked})}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h4>General Announcements</h4>
                    <p className="text-sm text-muted-foreground">
                      Company news and portal updates
                    </p>
                  </div>
                  <Switch
                    checked={notifications.generalAnnouncements}
                    onCheckedChange={(checked) => setNotifications({...notifications, generalAnnouncements: checked})}
                  />
                </div>
              </div>

              {/* Email & Priority Settings */}
              <div className="space-y-4">
                <div className="pb-2 border-b border-border">
                  <h4 className="font-medium text-sm">Email & Priority Settings</h4>
                  <p className="text-xs text-muted-foreground">Configure how you receive email notifications</p>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h4>High Priority Email Only</h4>
                    <p className="text-sm text-muted-foreground">
                      Only receive emails for high-priority notifications
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={notifications.highPriorityOnly}
                      onCheckedChange={(checked) => setNotifications({...notifications, highPriorityOnly: checked})}
                    />
                    <Badge className={notifications.highPriorityOnly ? 'bg-worldsview-red text-white' : 'bg-muted text-muted-foreground'}>
                      {notifications.highPriorityOnly ? 'High Priority Only' : 'All Priorities'}
                    </Badge>
                  </div>
                </div>


              </div>


            </CardContent>
          </Card>
        </TabsContent>




      </Tabs>
    </div>
  );
}